import dataURLtoBlob from "blueimp-canvas-to-blob";
import {csrfFetch} from '../../../util/CsrfProtection';

const FILE_API_BASE_URL = '/api/file';
const getUpdateGalleryUrl = (activityId: string): string => `/api/inventory-activity/${encodeURIComponent(activityId)}/gallery`;
const GET_INVENTORY_UPLOAD_URL = `${FILE_API_BASE_URL}/v2/inventory/upload-url`;
const GET_GALLERY_UPLOAD_URL = `${FILE_API_BASE_URL}/gallery/upload-url`;
const GET_LISTING_IMAGES_URL = `${FILE_API_BASE_URL}/listing-images/upload-url`
const GET_LEGAL_AGREEMENTS_URL = `${FILE_API_BASE_URL}/legal-agreements/upload-url`
const LISTING_IMAGES_FOLDER = 'listing-images';
const MAX_FILE_NAME_LENGTH = 255;
const MAX_EXTENSION_LENGTH = 16;
// eslint-disable-next-line no-control-regex -- control characters are exactly what the file API rejects
const FILE_NAME_CHARS_REJECTED_BY_API = /[\\/:%\u0000-\u001f\u007f-\u009f]/g;
const UUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
// The API assigns listing images "{accountId}_{uuid}.{ext}"; older client-chosen names can be shared by other records.
const SERVER_ASSIGNED_LISTING_IMAGE_NAME = new RegExp(`^${UUID_PATTERN}_${UUID_PATTERN}\\.[a-z0-9]+$`, 'i');

/** Response body of the `.../upload-url` endpoints */
interface UploadUrlResponse {
  /** Pre-signed URL the file is PUT to */
  uploadUrl: string;
  /** Server-assigned file name to persist */
  fileName: string;
}

// This flow uses the fetch wrapper because the upload itself is a PUT to a pre-signed storage URL.
// The wrapper adds CSRF only to same-origin API mutations, never to the external storage request.
const handleResponse = (response: Response): Response => {
  if (!response.ok) {
    throw Error(`${response.status} - ${response.statusText}`);
  }
  return response;
}

/**
 * Rewrites the characters the file API rejects (path separators, `:`, `%`, `..` and control characters) and keeps the
 * name within its 255-character limit, so a photo named e.g. "100% done.png" still uploads. The API returns the name
 * it stored, which is what callers must persist.
 */
export const toUploadFileName = (fileName: string): string => {
  const safeName = fileName.replace(FILE_NAME_CHARS_REJECTED_BY_API, '-').replace(/\.{2,}/g, '.');
  if (safeName.length <= MAX_FILE_NAME_LENGTH) {
    return safeName;
  }
  const dot = safeName.lastIndexOf('.');
  const extension = dot > 0 && safeName.length - dot <= MAX_EXTENSION_LENGTH ? safeName.slice(dot) : '';
  // Cutting the stem can leave it ending in '.', which would put '..' before the extension.
  const stem = safeName.slice(0, MAX_FILE_NAME_LENGTH - extension.length).replace(/\.+$/, '');
  return stem + extension;
}

const getUploadUrl = async (url: string, params: Record<string, string> & { fileName: string }): Promise<UploadUrlResponse> => {
  try {
    const response = await csrfFetch(url + '?' + new URLSearchParams({ ...params, fileName: toUploadFileName(params.fileName) }));
    return await handleResponse(response).json();
  } catch (_error) {
    throw Error("Failed to retrieve upload URL");
  }
}

const uploadFile = async (file: Blob, uploadUrl: string, contentType: string): Promise<void> => {
  try {
    const response = await csrfFetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": contentType
      },
      body: file
    });
    handleResponse(response);
  } catch (_e) {
    throw Error("Failed to upload file");
  }
}

export const uploadInventoryImageBlob = async (inventoryId: string, file: Blob, fileName: string, contentType: string): Promise<string> => {
  const { uploadUrl, fileName: responseFileName } = await getUploadUrl(GET_INVENTORY_UPLOAD_URL,
    {
      fileName,
      inventoryId,
      contentType
    });

  await uploadFile(file, uploadUrl, contentType);
  return responseFileName;
}

export const uploadInventoryImage = async (inventoryId: string, fileDataUrl: string, fileName: string, contentType: string): Promise<string> => {
  return uploadInventoryImageBlob(
    inventoryId,
    dataURLtoBlob(fileDataUrl),
    fileName,
    contentType
  );
}

export const uploadGalleryImage = async (locationId: string, fileDataUrl: string, fileName: string, contentType: string): Promise<string> => {
  const { uploadUrl, fileName: responseFileName } = await getUploadUrl(GET_GALLERY_UPLOAD_URL,
    {
      fileName,
      locationId,
      contentType
    });

  await uploadFile(dataURLtoBlob(fileDataUrl), uploadUrl, contentType);
  return responseFileName;
}

export const uploadListingImage = async (fileDataUrl: string, fileName: string, contentType: string): Promise<string> => {
  const { uploadUrl, fileName: responseFileName } = await getUploadUrl(GET_LISTING_IMAGES_URL,
    {
      fileName,
      contentType
    });

  await uploadFile(dataURLtoBlob(fileDataUrl), uploadUrl, contentType);
  return responseFileName;
}

export const uploadCompanyProfileImage = async (file: Blob, fileName: string, contentType: string): Promise<string> => {
  const { uploadUrl, fileName: responseFileName } = await getUploadUrl(GET_LISTING_IMAGES_URL,
    {
      fileName,
      contentType
    });

  await uploadFile(file, uploadUrl, contentType);
  return responseFileName;
}

export const uploadLegalAgreement = async (file: Blob, fileName: string, contentType: string): Promise<string> => {
  const { uploadUrl, fileName: responseFileName } = await getUploadUrl(GET_LEGAL_AGREEMENTS_URL,
    {
      fileName,
      contentType
    });

  await uploadFile(file, uploadUrl, contentType);
  return responseFileName;
}

export const deleteFile = async (fileName: string): Promise<void> => {
  await csrfFetch(FILE_API_BASE_URL + '?' + new URLSearchParams({ fileName }), { method: "DELETE" })
}

/**
 * Best-effort cleanup of a listing image that a successful save has replaced; never rejects. Only server-assigned
 * names are deleted, because older client-chosen names may still be referenced by another account or location.
 */
export const deleteReplacedListingImage = async (fileName?: string | null): Promise<void> => {
  if (!fileName || !SERVER_ASSIGNED_LISTING_IMAGE_NAME.test(fileName)) {
    return;
  }
  try {
    await deleteFile(`${LISTING_IMAGES_FOLDER}/${fileName}`);
  } catch (_error) {
    // The replaced object is only orphaned; the saved record already points at the new image.
  }
}


// J10: updated to use new path-param endpoint; activityId is now a path param
export const updateGallery = async (activityId: string, galleryFiles: string[]): Promise<Response> => {
  const response = await csrfFetch(getUpdateGalleryUrl(activityId), {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      "Accept": "application/json",
    },
    body: JSON.stringify({ galleryFiles }),
  });
  return handleResponse(response);
}
